import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../services/api';
import TaskFormDialog from '../components/TaskFormDialogV2';
import { TaskListItem, TaskMetaResponse } from '../types/task';

const TasksPageV2: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState<TaskMetaResponse | null>(null);
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  // Dashboard links pass ?status=<code>; otherwise show all open tasks
  const [status, setStatus] = useState<string>(searchParams.get('status') ?? 'open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);

  const statusLabel = useMemo(() => {
    const m = new Map((meta?.statuses || []).map((s) => [s.code, s.name]));
    return (code?: string | null) => (code ? m.get(code) || code : '');
  }, [meta]);

  const typeLabel = useMemo(() => {
    const m = new Map((meta?.types || []).map((t) => [t.code, t.name]));
    return (code?: string | null) => (code ? m.get(code) || code : '');
  }, [meta]);

  const getStatusColor = (code?: string | null): 'default' | 'info' | 'warning' | 'success' | 'error' => {
    switch (code) {
      case 'new':         return 'info';
      case 'in_progress': return 'warning';
      case 'completed':   return 'success';
      case 'closed':      return 'default';
      default:            return 'default';
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [m, list] = await Promise.all([
        apiService.getTaskMeta(),
        apiService.listTasks({ page: 1, per_page: 200, status }),
      ]);
      setMeta(m);
      setTasks(list.tasks || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Internal tasks linked to accounts and cases
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New task
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(String(e.target.value))}>
                <MenuItem value="open">All Open</MenuItem>
                <MenuItem value="">All statuses</MenuItem>
                {(meta?.statuses || []).map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {tasks.length} tasks
            </Typography>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Task ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Case / Ticket</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography
                      component={RouterLink}
                      to={`/crm/tasks/${t.id}`}
                      sx={{ textDecoration: 'none', fontWeight: 600, color: 'primary.main', fontSize: '0.85rem' }}
                    >
                      {t.task_number || t.id.slice(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>{t.title}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t.account_id ? (
                      <Typography
                        component={RouterLink}
                        to={`/crm/accounts/${t.account_id}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', fontSize: '0.85rem' }}
                      >
                        {t.account_display_name || t.account_sonic_number || t.account_id.slice(0, 8)}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t.case_id ? (
                      <Typography
                        component={RouterLink}
                        to={`/crm/cases/${t.case_id}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', fontSize: '0.85rem' }}
                      >
                        {t.case_number || 'Case'}
                      </Typography>
                    ) : t.support_desk_ticket_id ? (
                      <Typography
                        component={RouterLink}
                        to={`/crm/support-desk/${t.support_desk_ticket_id}`}
                        sx={{ textDecoration: 'none', color: 'primary.main', fontSize: '0.85rem' }}
                      >
                        {t.ticket_number || 'Ticket'}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{typeLabel(t.type_code)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel(t.status_code)} color={getStatusColor(t.status_code)} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.due_at ? new Date(t.due_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))}
              {tasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TaskFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          fetchAll();
        }}
      />
    </Box>
  );
};

export default TasksPageV2;

